<script>
/**
 * A generic table row component that is meant to be used in conjunction with the DataTable.vue component that can be
 * found in this directory. The template for this component is a <tr> tag that has a <slot> inside of it for you to
 * add whatever <td> tags you want in another component. The slot is surrounded by two <td> tags, one to the left
 * of it that contains a checkbox input for de/selecting, and one to the right of the slot that contains an
 * "action" panel. This action panel holds a menu of actions you can perform on this data such as editing,
 * or deleting things and expects the :actions prop to be passed to it.
 *
 * This component can be used directly, but the idea is that you override it with another component that
 * provides useful data to the user.
 *
 * *******
 * Popup Form
 * *******
 * If there are multiple items in the :actions prop, a button will appear that will open up a popup form thing with
 * all possible actions. When clicked, an event of the same name as the action will be emitted.
 *
 * ********
 * Single :actions
 * ********
 * If there is only one action in the :actions prop we don't display the popup form, instead just display the action
 * directly using an <a> tag
 */

import { Link } from '@inertiajs/vue3'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { DotsHorizontalIcon } from '@heroicons/vue/solid'
import ActionMenu from '@/Components/Table/ActionMenu'
import RowProps from '@/Components/Table/Mixins/RowProps'
import _capitalize from '@/Filters/Capitalize'

export default {
  mixins: [RowProps],
  components: { Link, Popover, PopoverButton, PopoverPanel, DotsHorizontalIcon, ActionMenu },

  data() {
    return {
      checked: false
    }
  },

  computed: {
    /**
     * Returns the number of items in the :actions prop
     * @returns {number}
     */
    actionCount() {
      return this.actions.length
    }
  },

  methods: {
    // Filter used to capitalize strings. Vue 3 removed filters so it goes here I guess
    _capitalize,

    /**
     * Called when the change event on the checkbox fires
     * @param event
     */
    rowChanged(event) {
      this.$emit('selected', this.index, event.target.checked)
    }
  }
}
</script>

<template>
  <tr>
    <td v-if="selectable">
      <input
        v-if="selectable"
        type="checkbox"
        class="form-checkbox"
        :checked="selected"
        @change="rowChanged"
      />
    </td>

    <slot />

    <td v-if="actionCount > 0">
      <Popover v-if="actionCount > 1">
        <PopoverButton class="btn btn-secondary-outline hover:bg-gray-300">
          <DotsHorizontalIcon
            class="h-4 w-4"
            aria-hidden="true"
          />
        </PopoverButton>

        <PopoverPanel class="absolute z-50 right-6 -mt-4">
          <action-menu
            v-bind="$attrs"
            :actions="actions"
            :can="can"
          />
        </PopoverPanel>
      </Popover>

      <a
        v-if="actionCount === 1"
        @click.prevent="$emit(actions[0])"
        class="text-blue-600 cursor-pointer"
      >
        {{ _capitalize(actions[0]) }}
      </a>
    </td>
  </tr>
</template>
